import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Tooltip } from "react-leaflet";

import { CalendarRange, MapPin, Gift } from "lucide-react";

import api from "../api";
import { track } from "../utils/analytics";
import CopyrightFooter from "../components/CopyrightFooter";
import LaunchBanner from "../components/LaunchBanner";
import Header from "../components/Header";
import Testimonials from "../components/Testimonials";

import heroCouple from "../assets/test_hero_image.webp";
import heartImage from "../assets/heart.png";

import { venueDetailsMapping } from "../misc/venueDetailsMapping";
import { pinkIcon } from "../components/MapIcons";

import "../styles/LandingPage.css";
import "../styles/buttons.css";

const LandingPage = () => {
  /* ───────────────────────────── state & helpers ─────────────────────────── */

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    topic: "general",
    phone: "",
    company: "",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mapHeight, setMapHeight] = useState("700px");

  const navigate = useNavigate();

  // venues with lat/lon
  const allVenues = Object.entries(venueDetailsMapping)
    .filter(([, v]) => v.latitude && v.longitude)
    .map(([id, v]) => ({
      id,
      lat: v.latitude,
      lon: v.longitude,
      title: v.title,
      caption: v.caption,
      imageUrl: v.imageUrl,
    }));

  /* ───────────────────────────── navigation ─────────────────────────────── */
  const goToSignup = () => {
    track("nav_click", { location: "banner_or_hero_signup" });
    navigate("/signup");
  };

  const goToCouplesPlans = () => {
    track("nav_click", { location: "couples_pricing" });
    navigate("/pricing/couples#couples-plans");
  };

  const goToGifting = () => {
    track("nav_click", { location: "gifting_page" });
    navigate("/gifting");
  };

  const goToBusinessPlans = () => {
    track("nav_click", { location: "business_pricing" });
    navigate("/pricing/businesses#business-plans");
  };

  /* ───────────────────────────── contact form ───────────────────────────── */
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/contact_form/", {
        name: formData.name,
        email: formData.email,
        // backend accepts "text" (alias) so you don't have to change the model everywhere else
        text: formData.message,
        topic: formData.topic || "general",
        phone: formData.phone || undefined,
        honeypot: formData.company || "", // hidden anti-bot field
      });

      if (data?.status === "success") {
        setIsSubmitted(true);
        track("generate_lead", {
          lead_source: "landing_contact_form",
          request_id: data.request_id,
        });
      } else {
        setError(
          data?.user_message || "Something went wrong. Please try again."
        );
        track("generate_lead_error", {
          reason: data?.user_message || "unknown",
        });
      }
    } catch (err) {
      setError("Could not submit. Please try again in a minute.");
      track("generate_lead_error", { reason: err?.message || "network" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const setH = () => {
      const w = window.innerWidth;
      if (w <= 560) setMapHeight("360px");
      else if (w <= 900) setMapHeight("520px");
      else setMapHeight("700px");
    };
    setH();
    window.addEventListener("resize", setH);
    return () => window.removeEventListener("resize", setH);
  }, []);

  /* ───────────────────────────── render ─────────────────────────────────── */
  return (
    <div className="landing-page">
      {/* Top announcement bar */}
      <LaunchBanner goToSignup={goToSignup} />
      <Header />

      {/* =====================   HERO   ===================== */}
      <header className="hero-fullbleed">
        <img src={heroCouple} alt="" className="hero-art" />

        <div className="heart-animation">
          <img src={heartImage} alt="" className="heart-image" />
        </div>

        <div className="hero-strip">
          <div className="hero-card">
            <h1 className="hero-headline">
              <span className="hl-main">
                Reliable 1-year Ahead Weather Forecasts
              </span>{" "}
              for <span className="hl-accent">Perfect Weddings</span>
            </h1>

            <p className="hero-sub">
              Pick your date with confidence and dodge surprise showers.
            </p>

            <ul className="value-list">
              <li>1 year‑ahead localized forecasts</li>
              <li>50+ CA venues</li>
              <li>AI‑powered accuracy</li>
            </ul>

            <div className="hero-cta">
              <button
                className="btn btn-primary btn-auto"
                onClick={() => {
                  track("main_cta_click", { message: "hero_check_my_venue" });
                  navigate("/check-venue");
                }}
              >
                Check my venue
              </button>
              <button
                className="btn btn-secondary btn-auto"
                onClick={() => {
                  track("secondary_cta_click", { message: "hero_watch_demo" });
                  navigate("/how_it_works");
                }}
              >
                Watch demo
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* =====================   FEATURES   ===================== */}
      <section className="features-section">
        <ul className="features-grid section-container">
          {[
            {
              title: "12-Month Outlook",
              desc: "See the forecast up to 365 days ahead for any supported venue.",
              Icon: CalendarRange,
            },
            {
              title: "Venue-Specific Accuracy",
              desc: "We create forecasts using machine-learning models powered by Climformatics.",
              Icon: MapPin,
            },
            {
              title: "Giftable Access",
              desc: "Share peace of mind with friends—plans are instantly transferable.",
              Icon: Gift,
            },
          ].map(({ title, desc, Icon }) => (
            <li key={title} className="feature-item">
              <Icon className="feature-icon" size={36} strokeWidth={2.2} />
              <div className="feature-copy">
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* =====================   OFFER CARDS   ===================== */}
      <section id="products" className="what-we-offer">
        <div className="section-container">
          <h2 className="section-title">Pick the plan that fits</h2>
          <div className="offer-cards">
            {[
              {
                title: "Couples",
                text: "Browse venue forecasts up to 12 months ahead & lock the perfect date.",
                onClick: goToCouplesPlans,
                buttonText: "Try free",
                id: "couples_try_now",
              },
              {
                title: "Venues & Planners",
                text: "Deliver flawless events with year-ahead insights and live alerts.",
                onClick: goToBusinessPlans,
                buttonText: "Demo",
                id: "business_try_now",
              },
              {
                title: "Gift a Plan",
                text: "Give engaged friends the certainty of sunshine on their special day.",
                onClick: goToGifting,
                buttonText: "Gift now",
                id: "gift_now",
              },
            ].map((card) => (
              <div key={card.id} className="offer-card">
                <div className="card-content">
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                  <div className="card-button-container">
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        track("select_content", {
                          content_type: "cta",
                          item_id: card.id,
                        });
                        card.onClick();
                      }}
                    >
                      {card.buttonText}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =====================   TESTIMONIALS   ===================== */}
      <Testimonials />

      {/* =====================   LOCATIONS   ===================== */}
      <section id="locations" className="locations-section">
        <div className="section-container">
          <h2 className="section-title">Our Locations</h2>
          <p className="locations-caption">
            Currently covering California—with more states on the way. Zoom the
            map or send us your dream venue.
          </p>

          <div className="locations-map-wrapper">
            <MapContainer
              center={[37.5, -119.5]}
              zoom={6}
              scrollWheelZoom
              style={{ height: mapHeight, width: "100%" }}
              eventHandlers={{
                zoomend: (e) => {
                  const map = e.target;
                  track("map_zoom", { zoom: map.getZoom() });
                },
                dragend: (e) => {
                  const map = e.target;
                  const center = map.getCenter();
                  track("map_move", { lat: center.lat, lon: center.lng });
                },
              }}
            >
              <TileLayer
                attribution="&copy; OpenStreetMap"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {allVenues.map((v) => (
                <Marker
                  key={v.id}
                  position={[v.lat, v.lon]}
                  icon={pinkIcon}
                  eventHandlers={{
                    click: () =>
                      track("map_marker_click", {
                        venue_id: v.id,
                        venue_title: v.title,
                      }),
                  }}
                >
                  <Tooltip direction="top" offset={[0, -20]} opacity={1}>
                    <div className="tooltip-content">
                      {v.imageUrl && (
                        <img
                          src={v.imageUrl}
                          alt={v.title}
                          className="tooltip-image"
                        />
                      )}
                      <strong>{v.title}</strong>
                      <p>{v.caption}</p>
                    </div>
                  </Tooltip>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>
      </section>

      {/* =====================   CONTACT   ===================== */}
      <section className="contact-section">
        <div className="section-container">
          <div className="contact-grid">
            <div className="contact-form">
              {!isSubmitted ? (
                <form onSubmit={handleContactSubmit}>
                  <h2 className="form-title">Request a venue or contact us</h2>
                  <p className="hint-text">
                    Missing your dream location? Tell us below and we’ll add it
                    within 24 hours.
                  </p>

                  {/* hidden honeypot (bots fill this; humans never see it) */}
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={(e) =>
                      setFormData({ ...formData, company: e.target.value })
                    }
                    autoComplete="off"
                    tabIndex={-1}
                    aria-hidden="true"
                    style={{ position: "absolute", left: "-9999px" }}
                  />

                  {/* Name + Email */}
                  {[
                    {
                      name: "name",
                      type: "text",
                      placeholder: "Full Name",
                      minLength: 2,
                    },
                    {
                      name: "email",
                      type: "email",
                      placeholder: "Email Address",
                    },
                  ].map((field) => (
                    <div key={field.name} className="form-group">
                      <input
                        type={field.type}
                        name={field.name}
                        placeholder={field.placeholder}
                        value={formData[field.name]}
                        required
                        minLength={field.minLength}
                        onFocus={() =>
                          track("contact_form_focus", { field: field.name })
                        }
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            [field.name]: e.target.value,
                          })
                        }
                      />
                    </div>
                  ))}

                  {/* Topic (helps route internally) */}
                  <div className="form-group">
                    <div className="select-field">
                      <select
                        name="topic"
                        value={formData.topic}
                        onFocus={() =>
                          track("contact_form_focus", { field: "topic" })
                        }
                        onChange={(e) =>
                          setFormData({ ...formData, topic: e.target.value })
                        }
                        aria-label="Topic"
                      >
                        <option value="general">General</option>
                        <option value="Venue Request">Venue Request</option>
                        <option value="sales">Sales</option>
                        <option value="partnerships">Partnerships</option>
                        <option value="press">Press</option>
                        <option value="bug">Bug report</option>
                      </select>
                    </div>
                  </div>

                  {/* Optional phone */}
                  <div className="form-group">
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone (optional)"
                      value={formData.phone}
                      onFocus={() =>
                        track("contact_form_focus", { field: "phone" })
                      }
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                    />
                  </div>

                  {/* Message */}
                  <div className="form-group">
                    <textarea
                      name="message"
                      placeholder="Message"
                      value={formData.message}
                      required
                      minLength={10}
                      onFocus={() =>
                        track("contact_form_focus", { field: "message" })
                      }
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                    />
                  </div>

                  {/* Error */}
                  {error && (
                    <div className="hint-text" role="alert" aria-live="polite">
                      {error}
                    </div>
                  )}

                  <div className="form-button-container">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                      onClick={() => track("contact_form_submit_click")}
                    >
                      {loading ? "Submitting…" : "Submit"}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="success-message">
                  <h2>Thank you!</h2>
                  <p>We’ll be in touch soon ✉️</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <CopyrightFooter
        className="landing-page-footer"
        onClick={() => track("footer_click", { location: "landing_page" })}
      />
    </div>
  );
};

export default LandingPage;
